import type { EditorState } from "../../vendor/src/features/remotion-editor/editor/state/types";
import { parseClipboardGraph, type ClipboardGraph } from "./clipboard-graph";
import {requiredClipboardVersion} from './clipboard-graph';

export type ClipboardPayload =
  | ClipboardGraph
  | { format: "invalid-clipboard" }
  | { format: "legacy-items"; items: Record<string, unknown>[] };
const record = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" && v !== null && !Array.isArray(v);
export function clipboardHtml(graph: ClipboardGraph): string {
  return `<div data-waka-editor-clipboard="${encodeURIComponent(JSON.stringify(graph)).replace(/'/g, "%27")}"></div>`;
}
export function parseClipboardHtml(text: string): ClipboardPayload | null {
  const invalid: ClipboardPayload | null =
    /data-(waka-editor-clipboard|remotion-editor-starter)/i.test(text)
      ? { format: "invalid-clipboard" }
      : null;
  if (text.length > 16_000_000) return invalid;
  try {
    // Only read the exact encoded attribute; never render or execute pasted HTML.
    const value = text.match(/data-waka-editor-clipboard\s*=\s*"([^"]+)"/)?.[1];
    if (value)
      return (
        parseClipboardGraph(JSON.parse(decodeURIComponent(value))) ?? invalid
      );
    const legacy = text.match(
      /data-remotion-editor-starter\s*=\s*"([^"]+)"/,
    )?.[1];
    const raw = legacy
      ? decodeURIComponent(legacy)
      : text.match(/<div data-remotion-editor-starter[^>]*>(.*)<\/div>/s)?.[1];
    if (!raw) return invalid;
    const items: unknown = JSON.parse(raw);
    return Array.isArray(items) &&
      items.length > 0 &&
      items.length <= 10000 &&
      items.every(record)
      ? { format: "legacy-items", items }
      : invalid;
  } catch {
    return invalid;
  }
}
/** Legacy clipboard data can only recover descendants/assets still present in the destination document. */
export function resolveClipboardPayload(
  state: EditorState,
  payload: ClipboardPayload,
): ClipboardGraph | null {
  if (payload.format !== "legacy-items") return parseClipboardGraph(payload);
  const items: Record<string, unknown> = {},
    assets: Record<string, unknown> = {};
  const visit = (
    item: Record<string, unknown>,
    seen = new Set<string>(),
  ): boolean => {
    if (
      typeof item.id !== "string" ||
      ["__proto__", "constructor", "prototype"].includes(item.id) ||
      seen.has(item.id) ||
      items[item.id]
    )
      return false;
    items[item.id] = item;
    if (typeof item.assetId === "string") {
      const asset = state.undoableState.assets[item.assetId];
      if (!asset) return false;
      assets[asset.id] = asset;
    }
    if (item.type !== "group") return true;
    return (
      Array.isArray(item.children) &&
      item.children.every((id) => {
        if (typeof id !== "string") return false;
        const child = state.undoableState.items[id];
        return (
          record(child) && visit(child, new Set(seen).add(String(item.id)))
        );
      })
    );
  };
  if (!payload.items.every((item) => visit(item))) return null;
  const roots = payload.items.map((item) => String(item.id));
  const tracks = payload.items.map((item, index) => ({
    id: `legacy-clipboard-track-${index}`,
    items: [String(item.id)],
    zone: item.type === "audio" ? "audio" : "visual",
    locked: false,
    hidden: false,
    muted: false,
    magnetic: false,
  }));
  try {
    return parseClipboardGraph(
      JSON.parse(
        JSON.stringify({
          format: "waka-editor-clipboard",
          version: requiredClipboardVersion({items}),
          fps: state.undoableState.fps,
          roots,
          items,
          assets,
          tracks,
          transitions: {},
        }),
      ),
    );
  } catch {
    return null;
  }
}
